import { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Images, MapPin, X } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import IMG, { caledon, cext, mcguire, mis, mssouth, orangeville } from "@/lib/images";

const CALEDON_CAPTIONS = {
    1: "Open living space — light grey & white trim",
    2: "Great room — floor-to-ceiling windows",
    3: "Great room & stone fireplace",
    4: "Hallway — white trim & doors",
    5: "Bedroom — light grey with white trim",
    6: "Open room — beams & fresh walls",
    7: "Bedroom — window detail",
    8: "Bedroom — white finish",
    9: "Closet & hallway — white finish",
    10: "Staircase — timber hall",
    11: "Built-in cabinetry & media wall",
    12: "Entrance hall — painted & tidy",
    13: "Main living area — painting in progress",
    14: "Living area — in progress",
    15: "Staircase detailing — treads & rails",
    16: "Living room — fireplace feature",
    17: "Staircase — charcoal accent wall",
    18: "Entry hallway — white finish",
    19: "Two-storey staircase — black accent wall",
    20: "Living area — finish coats",
    21: "Open living area — primed & prepped",
    22: "Family room — white coats in progress",
    23: "Window wall — stone & paint detail",
    24: "Dining nook — built-in seating",
    25: "Surface prep — primed & masked",
    26: "Staircase — prep stage",
    27: "Staircase — prep stage",
    28: "Great room — primer & masking",
    29: "Hall — prepped for paint",
    30: "Hallway — prepped for finish coats",
    31: "Beam detail — prepped",
    32: "Hallway — doors taped & prepped",
    33: "Skylight room — primer",
    34: "Bath niche & bench — prep",
    35: "Timber ceiling — prepped",
    36: "Family room — skylight & patio doors, primed",
    37: "Room prep — floor protection",
    38: "Great room — vaulted ceiling prep",
};

// Painted rooms first, prep shots at the end
const SHOWCASE_ORDER = [3, 17, 19, 11, 24, 5, 16, 15, 1, 2, 4, 9, 10, 12, 13, 14, 20, 6, 7, 8, 18, 22, 23, 21, 36, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 37, 38];

const M_CAPTIONS = {
    1: "Primary bathroom — soft grey & crisp white trim",
    2: "Principal room — bay window, light grey walls",
    3: "Exterior facade — stone & brick residence, paint work underway",
    4: "Curved bay-window wall — light grey finish",
    5: "Great room — vaulted detail, winter view",
    6: "Living area — modern fireplace & tray ceiling",
    7: "Grand foyer — double staircase & chandelier",
    8: "Bedroom — soft neutral finish",
    9: "Living area — black stacked-stone fireplace",
    10: "Open-concept kitchen — white cabinetry",
    11: "Open living — wainscoting & sightlines",
    12: "Kitchen — island & ceiling detail",
    13: "Great room — floor-to-ceiling windows",
    14: "Kitchen & dining — marble-style tile",
    15: "Coffered ceiling — wainscoting & arched window",
    16: "Entrance hall — decorative moulding",
    17: "Bay window — detailed wainscoting",
    18: "Multi-storey foyer — crystal chandelier",
};

const MIS_CAPTIONS = {
    1: "Hallway — primer & prep",
    2: "Living area — drywall & primer",
    3: "Bedroom — primer, skylight",
    4: "Open living — prep stage",
    5: "Bedroom — prep & priming",
    6: "Bathroom — shower rough-in, prepped",
    7: "Primary bath — glass shower, primed",
    8: "Bedroom — prepped & primed",
    9: "Kitchen — first coats",
    10: "Living area — warm tan walls",
    11: "Bathroom — white walls, dark vanity",
    12: "Hallway — skylight, white paint",
    13: "Living room — warm accent wall",
    14: "Foyer — light grey with white trim",
    15: "Bedroom — large window, light grey",
    16: "Stairwell — light grey",
    17: "Hallway — open area, painted",
    18: "Basement — large finished room",
    19: "Hallway — detail work",
    20: "Corner room — light grey finish",
    21: "Entry hallway — painted",
    22: "Built-in closet — white finish",
    23: "Bedroom — soft green-grey",
    24: "Room — patio door, light grey",
    25: "Bedroom — light green, fresh coats",
    26: "Exterior — grey brick, red door",
};

const MIS_ORDER = [13, 14, 15, 18, 23, 25, 16, 17, 19, 20, 21, 24, 22, 12, 11, 10, 9, 1, 2, 3, 4, 5, 6, 7, 8, 26];

const MS_CAPTIONS = {
    1: "Exterior — the new build before interior work",
    2: "Spray application — white finish",
    3: "Spray painting — drop cloths down",
    4: "Hallway — spray work in progress",
    5: "Bathroom — marble-effect feature wall",
    6: "Hallway — clean lines, protected finishes",
    7: "Hallway — white throughout",
    8: "Living & kitchen — white paint",
    9: "Paneled door — crisp white finish",
    10: "Staircase — dark fireplace feature",
    11: "Bedroom — window protected",
    12: "Bedroom — materials staged",
    13: "Hallway — primer stage",
    14: "Closet — primer, floor protection",
    15: "Staircase — masked & protected",
    16: "Bedroom — light grey, first coats",
    17: "Bedroom — light grey, painted",
    18: "Bedroom — lavender feature",
    19: "Bedroom — light grey, early coats",
    20: "Bathroom — lavender walls, painted",
    21: "Closet — white finish",
    22: "Storage — white finish",
    23: "Family room — prep in progress",
    24: "Bedroom office — white & grey, painted",
    25: "Hallway — light blue-grey, painted",
    26: "Office — white, trim taped",
    27: "Ensuite — white with black shower",
    28: "Walk-in closet — white finish",
    29: "Large living area — blue-grey, painted",
    30: "Closet — white, dark flooring",
    31: "Bathroom — white, tub & vanity",
    32: "Den — lime green & black accent wall",
    33: "Stairwell — blue-grey, posts in progress",
    34: "Stairwell — railing detail work",
    35: "Staircase — railing supports, painted",
};

const MS_ORDER = [32, 18, 20, 5, 29, 25, 33, 34, 35, 17, 24, 27, 31, 21, 22, 28, 30, 9, 7, 6, 10, 12, 2, 3, 4, 8, 11, 26, 13, 14, 15, 16, 19, 23, 1];

const CX_CAPTIONS = {
    1: "Front entrance — charcoal siding, red door",
    2: "Work in progress — ladders up",
    3: "Side elevation — painted brick & deck",
    4: "Side elevation — siding detail",
    5: "Garage — charcoal finish",
    6: "Roofline work in progress",
    7: "Stone wall & landscaping",
    8: "Front facade — grey & red",
    9: "Front entrance — after paint",
    10: "Front facade — painted charcoal, final details underway",
    11: "Brickwork — painted detail",
    12: "Rear of house — before & partial paint",
    13: "Chimney — prep stage",
    14: "Side & shed — prep stage",
    15: "Before — original light grey siding",
    16: "Side elevation — before, scaffolding up",
    17: "Before — angled view, original light siding & shutters",
    18: "Modern side elevation — large window, finished",
    19: "Before — front & side, original siding & red shutters",
};

const CX_ORDER = [15, 16, 17, 19, 13, 14, 12, 2, 6, 10, 9, 8, 5, 3, 4, 7, 11, 1, 18];

const PROJECTS = [
    {
        id: "caledon",
        title: "Caledon Build",
        location: "Caledon, ON",
        kind: "Custom home · interior painting",
        scope: "Surface prep, priming and finish coats through a full custom home — timber-beam great room, stone fireplace, staircase with accent wall, built-ins and bedrooms.",
        cover: caledon(3),
        photos: SHOWCASE_ORDER.map((n) => ({ src: caledon(n), caption: CALEDON_CAPTIONS[n] })),
    },
    {
        id: "mcguire",
        title: "McGuire Trail, Caledon East",
        location: "Caledon East, ON",
        kind: "Luxury new construction · interior & exterior painting",
        scope: "Complete interior painting through a luxury custom home — grand foyer with double staircase, coffered ceilings, wainscoting, kitchen and baths — plus exterior facade work.",
        cover: mcguire(7),
        photos: [7, 18, 15, 17, 16, 11, 9, 6, 5, 13, 10, 14, 12, 1, 2, 4, 8, 3].map((n) => ({ src: mcguire(n), caption: M_CAPTIONS[n] })),
    },
    {
        id: "mississauga",
        title: "Mississauga",
        location: "Mississauga, ON",
        kind: "New construction · interior painting",
        scope: "Full interior of a new build — every room primed and finished in light greys, whites and soft accents, from drywall prep to final coats.",
        cover: mis(13),
        photos: MIS_ORDER.map((n) => ({ src: mis(n), caption: MIS_CAPTIONS[n] })),
    },
    {
        id: "mississauga-south",
        title: "Mississauga South",
        location: "Mississauga, ON",
        kind: "New construction · interior painting",
        scope: "Spray-applied whites throughout a large new home, with lavender, blue-grey and lime accent rooms, closets, stairwells and multiple bathrooms.",
        cover: mssouth(32),
        photos: MS_ORDER.map((n) => ({ src: mssouth(n), caption: MS_CAPTIONS[n] })),
    },
    {
        id: "caledon-exterior",
        title: "Caledon Exterior Painting",
        location: "Caledon, ON",
        kind: "New construction · exterior painting",
        scope: "Complete exterior transformation — charcoal siding, painted brick, garage and roofline work, documented from the original grey siding to the finished facade.",
        cover: cext(1),
        photos: CX_ORDER.map((n) => ({ src: cext(n), caption: CX_CAPTIONS[n] })),
    },
    {
        id: "foyer",
        title: "Two-storey Foyer",
        location: "From our Google listing",
        kind: "Residential · walls, wainscoting & staircase",
        scope: "Two-storey foyer — painted walls, crisp wainscoting and staircase detailing.",
        cover: IMG.googleFoyer,
        photos: [{ src: IMG.googleFoyer, caption: "Two-storey foyer — walls, wainscoting & staircase" }],
    },
];

function BeforeAfterSlider({ before, after, beforeAlt, afterAlt, afterPosition = "center" }) {
    const [pos, setPos] = useState(50);
    return (
        <div data-testid="portfolio-before-after-slider" className="relative select-none overflow-hidden border border-gold/20">
            <div className="relative h-72 sm:h-96">
                <img src={after} alt={afterAlt} className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: afterPosition }} />
                <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
                    <img src={before} alt={beforeAlt} className="absolute inset-0 h-full w-full object-cover" />
                </div>
                <div className="absolute inset-y-0 w-1 bg-gold" style={{ left: `${pos}%` }} />
                <input
                    type="range" min="5" max="95" value={pos} data-testid="portfolio-before-after-range"
                    onChange={(e) => setPos(Number(e.target.value))}
                    aria-label="Drag to compare before and after"
                    className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0"
                />
                <span className="absolute left-4 top-4 border border-white/30 bg-ink/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-200">Before</span>
                <span className="absolute right-4 top-4 border border-gold/50 bg-ink/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">After</span>
            </div>
            <p className="bg-ink-2 p-4 text-center font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
                Drag to compare — same view, before and after
            </p>
        </div>
    );
}

function Lightbox({ project, index, setIndex, onClose }) {
    const total = project.photos.length;
    const photo = project.photos[index];

    const next = useCallback(() => setIndex((i) => (i + 1) % total), [total, setIndex]);
    const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total, setIndex]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [next, prev, onClose]);

    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = ""; };
    }, []);

    useEffect(() => {
        const el = document.getElementById("portfolio-thumb-active");
        if (el) el.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
    }, [index]);

    return (
        <motion.div data-testid="portfolio-lightbox" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex flex-col bg-ink/97 p-4 backdrop-blur-md sm:p-8" onClick={onClose}>
            <div className="flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">{project.title} · {project.location}</p>
                <button onClick={onClose} data-testid="portfolio-lightbox-close" aria-label="Close" className="border border-white/20 p-3 text-slate-100 transition-colors hover:border-gold hover:text-gold"><X size={20} /></button>
            </div>
            <div className="relative mt-4 flex flex-1 items-center justify-center" onClick={(e) => e.stopPropagation()}>
                <button onClick={prev} data-testid="portfolio-lightbox-prev" aria-label="Previous photo"
                    className="absolute left-0 z-10 border border-white/20 bg-ink/70 p-3 text-slate-100 transition-colors hover:border-gold hover:text-gold sm:left-4">
                    <ChevronLeft size={22} />
                </button>
                <AnimatePresence mode="wait">
                    <motion.img key={photo.src} src={photo.src} alt={`${project.title} — ${photo.caption}`}
                        initial={{ opacity: 0, scale: 0.985 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.99 }}
                        transition={{ duration: 0.25 }}
                        className="max-h-[70vh] max-w-full object-contain" />
                </AnimatePresence>
                <button onClick={next} data-testid="portfolio-lightbox-next" aria-label="Next photo"
                    className="absolute right-0 z-10 border border-white/20 bg-ink/70 p-3 text-slate-100 transition-colors hover:border-gold hover:text-gold sm:right-4">
                    <ChevronRight size={22} />
                </button>
            </div>
            <div className="mt-4" onClick={(e) => e.stopPropagation()}>
                <p className="text-center font-display text-lg font-bold text-slate-50" data-testid="portfolio-lightbox-caption">{photo.caption}</p>
                <p className="mt-1 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500" data-testid="portfolio-lightbox-counter">{index + 1} / {total}</p>
                {total > 1 && (
                    <div className="mx-auto mt-4 flex max-w-full gap-2 overflow-x-auto pb-2 lg:justify-center">
                        {project.photos.map((p, i) => (
                            <button key={p.src} data-testid={`portfolio-thumb-${i}`} id={i === index ? "portfolio-thumb-active" : undefined}
                                onClick={() => setIndex(i)} aria-label={`Photo ${i + 1}: ${p.caption}`}
                                className={`h-14 w-20 shrink-0 overflow-hidden border-2 transition-opacity ${i === index ? "border-gold opacity-100" : "border-transparent opacity-45 hover:opacity-80"}`}>
                                <img src={p.src} alt="" className="h-full w-full object-cover" loading="lazy" />
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </motion.div>
    );
}

export default function Portfolio() {
    const [viewer, setViewer] = useState(null); // { project, photo }
    const open = viewer === null ? null : { project: PROJECTS[viewer.project], index: viewer.photo };

    const setIndex = (u) =>
        setViewer((v) => ({ project: v.project, photo: typeof u === "function" ? u(v.photo) : u }));

    return (
        <div data-testid="portfolio-page" className="pt-28 lg:pt-36">
            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                <Reveal>
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Portfolio</p>
                    <h1 className="font-display mt-5 max-w-3xl text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                        Real work, <span className="text-gold">from our job sites.</span>
                    </h1>
                    <p data-testid="portfolio-illustrative-note" className="mt-8 max-w-3xl text-sm leading-relaxed text-slate-400">
                        Every photo on this page is from our own projects. Browse each project as a
                        complete photo set — from surface prep through final coats.
                    </p>
                </Reveal>

                <div className="mt-12 grid gap-6 md:grid-cols-2">
                    {PROJECTS.map((p, idx) => (
                        <Reveal key={p.id} delay={(idx % 2) * 0.1} className={idx === 0 ? "md:col-span-2" : ""}>
                            <article data-testid={`portfolio-project-${p.id}`}
                                className={`spot-card group relative overflow-hidden border border-gold/15 ${idx === 0 ? "clip-frame" : "clip-frame-alt"}`}>
                                <div className="relative">
                                    <img src={p.cover} alt={`${p.title} — our completed painting work`} loading="lazy"
                                        className={`w-full object-cover transition-transform duration-700 group-hover:scale-[1.03] ${idx === 0 ? "h-[420px] lg:h-[560px]" : "h-72"}`} />
                                    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
                                    <div className="absolute inset-x-0 bottom-0 p-7 sm:p-10">
                                        <span className="inline-flex items-center gap-2 border border-gold/40 bg-ink/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold backdrop-blur-sm">
                                            <MapPin size={11} /> Our work · {p.location}
                                        </span>
                                        <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl">{p.title}</h2>
                                        <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-300">{p.kind}</p>
                                        {idx === 0 && <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-300">{p.scope}</p>}
                                        <button
                                            onClick={() => setViewer({ project: idx, photo: 0 })}
                                            data-testid={`portfolio-open-${p.id}`}
                                            className="mt-6 inline-flex items-center gap-3 bg-gold px-7 py-3.5 font-display text-sm font-bold uppercase tracking-widest text-ink transition-transform hover:-translate-y-0.5"
                                        >
                                            <Images size={16} /> View project photos ({p.photos.length})
                                        </button>
                                    </div>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>

                <div className="mt-20 pb-24">
                    <Reveal>
                        <h2 className="font-display text-2xl font-bold sm:text-3xl">Before &amp; after — Caledon Exterior.</h2>
                        <p className="mt-3 max-w-2xl text-sm text-slate-400">
                            Drag the handle — the same front view of the house, from the original
                            light grey siding to the freshly painted charcoal facade.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1} className="mt-10">
                        <BeforeAfterSlider
                            before={cext(19)} after={cext(10)} afterPosition="center 55%"
                            beforeAlt="Before — original light grey siding and shutters, front view"
                            afterAlt="After — charcoal siding and painted brick, front view"
                        />
                    </Reveal>
                    <Reveal delay={0.15} className="mt-12">
                        <Link to="/contact" data-testid="portfolio-cta" className="group inline-flex items-center gap-3 bg-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-widest text-ink transition-transform hover:-translate-y-0.5">
                            Start your project <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            <AnimatePresence>
                {open && (
                    <Lightbox project={open.project} index={open.index} onClose={() => setViewer(null)} setIndex={setIndex} />
                )}
            </AnimatePresence>
        </div>
    );
}